import { useEffect, useState, useRef } from 'react';
import { Button, makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { getBrandConfig } from '../brands/brand-registry';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('8px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    marginBottom: '16px',
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: 'var(--text-primary-aaa, #1a1a1a)',
    marginBottom: '8px',
  },
  message: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: 'var(--text-secondary-aaa, #2d2d2d)',
    marginBottom: '12px',
  },
  countdown: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: 'var(--color-primary, #005EB8)',
    marginBottom: '12px',
    fontVariantNumeric: 'tabular-nums',
  },
  actions: {
    display: 'flex',
    ...shorthands.gap('12px'),
    flexWrap: 'wrap',
  },
  button: {
    minWidth: '120px',
    minHeight: '44px', // WCAG AAA touch target
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    ...shorthands.padding('0'),
    ...shorthands.margin('-1px'),
    ...shorthands.overflow('hidden'),
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    ...shorthands.border('0'),
  },
});

export interface RateLimitMessageProps {
  retryAfter: number; // seconds
  onRetry?: () => void;
  onExtension?: () => void;
  showExtensionButton?: boolean;
}

export function RateLimitMessage({
  retryAfter,
  onRetry,
  onExtension,
  showExtensionButton = true,
}: RateLimitMessageProps) {
  const styles = useStyles();
  const brandConfig = getBrandConfig();
  const rateLimitConfig = brandConfig.rateLimiting as {
    friendly: {
      title: string;
      message: string;
      countdown: string;
      screenReaderAnnouncement: string;
      extensionButton: string;
    };
    warning: {
      message: string;
      threshold: number;
    };
    accessibility: {
      errorRole: string;
      livePriority: string;
      countdownRole: string;
    };
  };
  
  const [secondsRemaining, setSecondsRemaining] = useState(retryAfter);
  const [announcement, setAnnouncement] = useState('');
  const intervalRef = useRef<number | null>(null);

  // Countdown timer
  useEffect(() => {
    setSecondsRemaining(retryAfter);
    
    // Announce to screen readers
    const initialAnnouncement = rateLimitConfig.friendly.screenReaderAnnouncement
      .replace('{seconds}', retryAfter.toString());
    setAnnouncement(initialAnnouncement);
    
    intervalRef.current = window.setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          if (onRetry) onRetry();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [retryAfter, rateLimitConfig.friendly.screenReaderAnnouncement, onRetry]);

  // Announce every 10 seconds or at key intervals
  useEffect(() => {
    if (secondsRemaining > 0 && (secondsRemaining % 10 === 0 || secondsRemaining <= 5)) {
      const countdownAnnouncement = rateLimitConfig.friendly.countdown
        .replace('{seconds}', secondsRemaining.toString());
      setAnnouncement(countdownAnnouncement);
    }
  }, [secondsRemaining, rateLimitConfig.friendly.countdown]);

  const countdownText = rateLimitConfig.friendly.countdown
    .replace('{seconds}', secondsRemaining.toString());

  return (
    <div
      className={styles.container}
      role={rateLimitConfig.accessibility.errorRole || 'alert'}
      aria-live={(rateLimitConfig.accessibility.livePriority || 'assertive') as 'assertive' | 'polite' | 'off'}
      aria-atomic="true"
    >
      {/* Screen reader announcements */}
      <div
        className={styles.srOnly}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </div>

      <div className={styles.title}>
        {rateLimitConfig.friendly.title}
      </div>

      <div className={styles.message}>
        {rateLimitConfig.friendly.message}
      </div>

      <div
        className={styles.countdown}
        role={rateLimitConfig.accessibility.countdownRole || 'timer'}
        aria-live="polite"
        aria-atomic="true"
      >
        {countdownText}
      </div>

      {showExtensionButton && onExtension && (
        <div className={styles.actions}>
          <Button
            appearance="primary"
            className={styles.button}
            onClick={onExtension}
            aria-label={rateLimitConfig.friendly.extensionButton}
          >
            {rateLimitConfig.friendly.extensionButton}
          </Button>
        </div>
      )}
    </div>
  );
}
